import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { odpWorkorderQuery, type OdpSku, type OdpStageCount } from '../../api/easeedeskDashboards'
import { Content } from '../../ui/Shell'
import { RANGES, rangeOf, type RangeName } from './range'
import { NotInstalled } from './WallHeader'

/**
 * "Services and Support Delivery Report" -
 * `easeedesk-dashboards/Odp_workorder::index` + `get_workorders_data`,
 * macheta `views/version-3/dashboards/easeedesk/sku/index.php` si casutele pe
 * care le desena `WorkorderSkus` din `public/js/dashboards/easeedesk.js`.
 *
 * Sus, sase casute: comenzile primite, tichetele rezolvate pe patru tipuri, si
 * procentul de anulari. Sub ele, cate o casuta pe SKU, cu patru cifre mari
 * (primite, activate, terminate, anulate) si opt mici (acasa / in magazin
 * pentru fiecare), pe aceleasi patru culori. Jos, aceeasi legenda.
 *
 * Numele grupului poarta acelasi semn ca in JS: `(SD + O)` cand SKU-ul are si
 * comenzi manuale, `(SD)` cand nu.
 */

const STAGES: { key: keyof Pick<OdpSku, 'sended' | 'activated' | 'finished' | 'cancelled'>; tone: string; icon: string }[] = [
  { key: 'sended', tone: 'bg-primary', icon: 'far fa-paper-plane' },
  { key: 'activated', tone: 'bg-warning', icon: 'fas fa-check' },
  { key: 'finished', tone: 'bg-success', icon: 'fa fa-flag-checkered' },
  { key: 'cancelled', tone: 'bg-danger', icon: 'fa fa-ban' },
]

const LEGEND = [
  { label: 'Received', color: '#0090d9' },
  { label: 'Activated', color: '#ffcc33' },
  { label: 'Finished', color: '#2ecc71' },
  { label: 'Cancelled', color: '#d9534f' },
]

const CARD = { border: '1px solid #ddd', backgroundColor: '#f8f8f8', marginTop: 15 }

const CARD_HEAD = {
  position: 'relative' as const,
  padding: 10,
  borderBottom: '1px solid #ddd',
  color: '#fff',
  backgroundColor: '#028900',
}

function Headline({ value, label, tone }: { value: string | number; label: string; tone: string }) {
  return (
    <div className="col-md-2">
      <div style={CARD}>
        <div className={tone} style={{ ...CARD_HEAD, textAlign: 'center' }}>
          <h4 style={{ display: 'inline-block', color: '#fff', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            <span style={{ fontSize: 35 }}>{value}</span>
            <br />
            {label}
          </h4>
        </div>
      </div>
    </div>
  )
}

function Cell({ count, tone, icon, size, width }: { count: number; tone: string; icon: string; size: number; width: string }) {
  return (
    <div
      className={tone}
      style={{ display: 'table-cell', padding: '4px 0', margin: 2, fontWeight: 'bold', fontSize: size, width }}
    >
      {count}
      <p style={{ fontSize: 16 }}>
        <i className={icon} aria-hidden="true" />
      </p>
    </div>
  )
}

function SkuCard({ sku }: { sku: OdpSku }) {
  const name = `${sku.group_name}${sku.manual_commands > 0 ? ' (SD + O)' : ' (SD)'}`

  return (
    <div className="col-md-2" data-sku={sku.sku_id}>
      <div style={CARD}>
        <div className="box-widget-header" style={CARD_HEAD}>
          <h4 title={name} style={{ display: 'inline-block', color: '#fff', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {name}
          </h4>
        </div>
      </div>
      <div style={{ display: 'table', width: '100%', textAlign: 'center' }}>
        {STAGES.map((stage) => (
          <Cell key={stage.key} count={(sku[stage.key] as OdpStageCount).all} tone={stage.tone} icon={stage.icon} size={30} width="25%" />
        ))}
      </div>
      <div style={{ display: 'table', width: '100%', textAlign: 'center' }}>
        {STAGES.map((stage) => (
          <span key={stage.key} style={{ display: 'contents' }}>
            <Cell count={(sku[stage.key] as OdpStageCount).home} tone={stage.tone} icon="fa fa-home" size={22} width="12.5%" />
            <Cell count={(sku[stage.key] as OdpStageCount).store} tone={stage.tone} icon="fa fa-shopping-cart" size={22} width="12.5%" />
          </span>
        ))}
      </div>
    </div>
  )
}

export function OdpWorkorderPage() {
  const [rangeName, setRangeName] = useState<RangeName>('Today')
  const q = useQuery(odpWorkorderQuery(rangeOf(rangeName)))
  const page = q.data
  const skus = page?.skus ?? []
  /* `(all_cancelled / all_sended) * 100`, cu doua zecimale, ca in JS. */
  const cancelled = page && page.all_sended > 0 ? ((page.all_cancelled / page.all_sended) * 100).toFixed(2) : '0'

  return (
    <Content>
      <div className="row" style={{ marginBottom: 20 }}>
        <div className="col-md-2 pull-right">
          <select
            className="form-control"
            aria-label="Date range"
            value={rangeName}
            onChange={(e) => setRangeName(e.target.value as RangeName)}
          >
            {RANGES.map((name) => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
          </select>
        </div>
        <div className="col-md-6">
          <h4>{page?.title ?? 'Services and Support Delivery Report'}</h4>
        </div>
      </div>

      <hr />

      {page && !page.ready ? <NotInstalled what="Office Depot workorder" /> : null}

      <div id="workorder">
        {skus.length === 0 ? (
          <div className="col-md-12 alert alert-info">
            <i className="fas fa-info-circle" aria-hidden="true" />
            No workorders found
          </div>
        ) : (
          <>
            <div className="row">
              <Headline value={page?.all_sended ?? 0} label="Received Orders" tone="bg-primary" />
              <Headline value={page?.resolved.operator ?? 0} label="Operator" tone="bg-success" />
              <Headline value={page?.resolved.support_request ?? 0} label="Support Request" tone="bg-success" />
              <Headline value={page?.resolved.chat ?? 0} label="Chat" tone="bg-success" />
              <Headline value={page?.resolved.phone_support ?? 0} label="Phone Call Support" tone="bg-success" />
              <Headline value={`${cancelled}%`} label="Cancelled" tone="bg-danger" />
            </div>

            <div className="row">
              {skus.map((sku) => (
                <SkuCard key={sku.sku_id} sku={sku} />
              ))}
            </div>

            <div className="row" id="dashboard-legend" style={{ padding: '0 15px', textAlign: 'center', marginTop: 15 }}>
              {LEGEND.map((item) => (
                <div key={item.label} style={{ display: 'inline', marginRight: 20, fontSize: 24, color: item.color }}>
                  <i className="fas fa-circle" aria-hidden="true" /> {item.label}
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </Content>
  )
}
